/**
 * Sessions management page.
 * Tabbed list with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { CalendarSyncModal } from '@/components/calendar/CalendarSyncModal';
import { CALENDAR_SYNC_ENABLED } from '@/config/features';
import { Button } from '@/components/forms/Button';
import { RefreshCw } from 'lucide-react';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { SessionsDataTable } from '@/components/sessions/inperson/SessionsDataTable';
import { CreateSessionView } from '@/components/sessions/inperson/CreateSessionView';
import { EditSessionView } from '@/components/sessions/inperson/EditSessionView';
import { SelectSessionTypeModal } from '@/components/sessions/inperson/SelectSessionTypeModal';
import { CreateTutorView } from '@/components/tutors/inperson/CreateTutorView';
import { EditTutorView } from '@/components/tutors/inperson/EditTutorView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import {
  DEFAULT_SESSION_FILTERS,
  SESSION_IN_PERSON_TAB_COLORS as TAB_COLORS,
  SESSION_TYPE_CONFIG,
  type SessionFilters,
} from '@/components/sessions/session-configs';
import {
  SESSION_IN_PERSON_TAB_LABELS as TAB_LABELS,
  SESSION_TAB_TO_TYPES,
} from '@/constants/shared-catalog';
import { toSessionInPersonTab } from '@/utils/common-utils';
import {
  type SessionTab,
  type PlanificationType,
  type SessionGroup,
} from '@/types/inperson/session.types';
import type { TabItem } from '@/components/ui/tabs';

/** Which form is mounted in the collapse wrapper (session, group, or nested tutor). */
type ActiveForm =
  | 'create'
  | 'edit'
  | 'edit-group'
  | 'create-information'
  | 'edit-information'
  | 'createTutor'
  | 'editTutor'
  | null;

/** Interface-element row used to decide which session tabs RBAC allows. */
type TabSourceElement = { elementType: string; elementId: string };

/** Tutor filter passed via location.state from another page. */
type PersonFilter = {
  tutorUserId?: string;
  personName?: string;
};

/** Callbacks used by {@link applySessionSearchParams} so the helper stays testable. */
type SearchParamHandlers = {
  setOpenRow: (id: string) => void;
  closeRow: () => void;
  setEditSessionId: (id: string | null) => void;
  setActiveForm: (form: ActiveForm) => void;
  setPlanificationModalOpen: (open: boolean) => void;
};

/** Props for {@link renderSessionFormContent}. */
type SessionFormContentProps = {
  activeForm: ActiveForm;
  planificationType: PlanificationType | null;
  editSessionId: string | null;
  editGroup: SessionGroup | null;
  editTutorId: string | null;
  onClose: () => void;
  onSessionSuccess: () => void | Promise<void>;
  onTutorSuccess: () => void;
  onRequestCreateTutor: () => void;
};

/** Tab ids in display order (RBAC subset is sorted back into this sequence). */
const TAB_ORDER: SessionTab[] = ['tutoring', 'information-meeting'];

/** RBAC-allowed tabs; `undefined` while elements load so the table does not query with the wrong types. */
function resolveAllowedSessionTabs(
  elements: TabSourceElement[],
  elementsLoading: boolean,
): SessionTab[] | undefined {
  if (elementsLoading) return undefined;
  const tabElements = elements.filter((el) => el.elementType === 'tab');
  if (tabElements.length === 0) return TAB_ORDER;
  return tabElements
    .map((el) => toSessionInPersonTab(el.elementId))
    .filter((id): id is SessionTab => id !== null)
    .sort((a, b) => TAB_ORDER.indexOf(a) - TAB_ORDER.indexOf(b));
}

/** Build colored tab items; icon comes from the first session type mapped to that tab. */
function buildSessionTabItems(allowedTabIds: SessionTab[] | undefined): TabItem[] {
  if (!allowedTabIds || allowedTabIds.length === 0) return [];
  return allowedTabIds.map((id) => {
    const firstType = SESSION_TAB_TO_TYPES[id]?.[0];
    const Icon = firstType ? SESSION_TYPE_CONFIG[firstType]?.icon : undefined;
    return {
      id,
      label: TAB_LABELS[id],
      icon: Icon ? <Icon className="h-4 w-4" /> : undefined,
      color: TAB_COLORS[id],
    };
  });
}

/** Read a tutor filter from router location.state. */
function readPersonFilter(state: unknown): PersonFilter {
  const parsed = state as PersonFilter | null;
  return { tutorUserId: parsed?.tutorUserId, personName: parsed?.personName };
}

/** Apply the tutor filter then clear location.state so refresh does not re-apply it. */
function applyPersonFilterState(
  state: unknown,
  pathname: string,
  setPersonFilter: (filter: PersonFilter) => void,
  navigate: (to: string, opts: { replace: boolean; state: Record<string, never> }) => void,
) {
  const filter = readPersonFilter(state);
  if (!filter.tutorUserId) return;
  setPersonFilter(filter);
  navigate(pathname, { replace: true, state: {} });
}

/** Default table filters; inject `tutorId` when arriving from a tutor deep-link. */
function buildDefaultSessionFilters(tutorUserId?: string) {
  if (!tutorUserId) return { ...DEFAULT_SESSION_FILTERS };
  return { ...DEFAULT_SESSION_FILTERS, tutorId: tutorUserId };
}

/** Deep-link: open create / edit / view from URL params (first matching param wins). */
function applySessionSearchParams(searchParams: URLSearchParams, handlers: SearchParamHandlers) {
  const editInformation = searchParams.get('edit-information');
  if (editInformation) {
    handlers.setEditSessionId(editInformation);
    handlers.setActiveForm('edit-information');
    handlers.closeRow();
    return;
  }

  const editId = searchParams.get('edit');
  if (editId) {
    handlers.setEditSessionId(editId);
    handlers.setActiveForm('edit');
    handlers.closeRow();
    return;
  }

  const viewId = searchParams.get('view');
  if (viewId) {
    handlers.setOpenRow(viewId);
    handlers.setActiveForm(null);
    handlers.setEditSessionId(null);
    return;
  }

  if (searchParams.get('create') !== 'true') return;
  handlers.setPlanificationModalOpen(true);
  handlers.setEditSessionId(null);
  handlers.closeRow();
}

/** Pick the edit form from session type, falling back to the active tab. */
function resolveSessionEditForm(
  sessionType: string | undefined,
  activeTab: SessionTab,
): Extract<ActiveForm, 'edit' | 'edit-information'> {
  if (sessionType === 'information_meeting_in_person' || activeTab === 'information-meeting') {
    return 'edit-information';
  }
  return 'edit';
}

/** After the type modal: information-meeting skips `planificationType`; others keep it for {@link CreateSessionView}. */
function applyPlanificationSelect(
  type: PlanificationType,
  setActiveForm: (form: ActiveForm) => void,
  setPlanificationType: (type: PlanificationType | null) => void,
  setPlanificationModalOpen: (open: boolean) => void,
  setEditSessionId: (id: string | null) => void,
  closeRow: () => void,
) {
  if (type === 'information_meeting') {
    setActiveForm('create-information');
    setPlanificationModalOpen(false);
    closeRow();
    return;
  }
  setPlanificationType(type);
  setPlanificationModalOpen(false);
  setActiveForm('create');
  setEditSessionId(null);
  closeRow();
}

/** Pick the collapse-wrapper form from `activeForm` (session / group / tutor). */
function renderSessionFormContent({
  activeForm,
  planificationType,
  editSessionId,
  editGroup,
  editTutorId,
  onClose,
  onSessionSuccess,
  onTutorSuccess,
  onRequestCreateTutor,
}: SessionFormContentProps): React.ReactNode {
  if (activeForm === 'create' && planificationType) {
    return (
      <CreateSessionView
        key={`sessions-create-${planificationType}`}
        planificationType={planificationType}
        onClose={onClose}
        onSuccess={onSessionSuccess}
        onRequestCreateTutor={onRequestCreateTutor}
      />
    );
  }
  if (activeForm === 'create-information') {
    return (
      <CreateSessionView
        key="sessions-create-information"
        kind="information_meeting"
        onClose={onClose}
        onSuccess={onSessionSuccess}
      />
    );
  }
  if (activeForm === 'edit' && editSessionId) {
    return (
      <EditSessionView
        key={`sessions-edit-${editSessionId}`}
        sessionId={editSessionId}
        onClose={onClose}
        onSuccess={onSessionSuccess}
        onRequestCreateTutor={onRequestCreateTutor}
      />
    );
  }
  if (activeForm === 'edit-information' && editSessionId) {
    return (
      <EditSessionView
        key={`sessions-edit-information-${editSessionId}`}
        kind="information_meeting"
        sessionId={editSessionId}
        onClose={onClose}
        onSuccess={onSessionSuccess}
      />
    );
  }
  if (activeForm === 'edit-group' && editGroup) {
    return (
      <CreateSessionView
        key={`sessions-edit-group-${editGroup.id}`}
        editGroup={editGroup}
        onClose={onClose}
        onSuccess={onSessionSuccess}
        onRequestCreateTutor={onRequestCreateTutor}
      />
    );
  }
  if (activeForm === 'createTutor') {
    return <CreateTutorView onSuccess={onTutorSuccess} onClose={onClose} />;
  }
  if (activeForm === 'editTutor' && editTutorId) {
    return (
      <EditTutorView
        key={editTutorId}
        tutorUserId={editTutorId}
        onSuccess={onTutorSuccess}
        onClose={onClose}
      />
    );
  }
  return null;
}

/** Scroll the page to the form when it first opens. */
function useScrollFormIntoView(
  activeForm: ActiveForm,
  contentRef: React.RefObject<HTMLDivElement | null>,
) {
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    const formOpened = prevActiveFormRef.current !== activeForm && activeForm !== null;
    if (formOpened && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm, contentRef]);
}

/** Banner shown when the list is filtered to one tutor's sessions. */
function PersonFilterBanner({
  personName,
  onReset,
}: Readonly<{
  personName?: string;
  onReset: () => void;
}>) {
  if (!personName) return null;
  return (
    <div className="mb-4 p-3 rounded-lg bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] border border-[var(--color-2-nuance-1)] flex items-center justify-between">
      <span className="text-sm font-medium" style={{ color: 'var(--color-2-nuance-1)' }}>
        Séances de {personName}
      </span>
      <Button variant="ghost" size="sm" onClick={onReset}>
        Réinitialiser
      </Button>
    </div>
  );
}

/** Header export + optional create button. */
function renderSessionPageActions(
  canCreate: boolean,
  onCreate: () => void,
  onOpenSync: () => void,
): React.ReactNode {
  return (
    <div className="flex items-center gap-3">
      {CALENDAR_SYNC_ENABLED ? (
        <Button preset="export" icon={RefreshCw} onClick={onOpenSync}>
          Synchroniser
        </Button>
      ) : null}
      {canCreate ? (
        <Button preset="create-entity" onClick={onCreate}>
          Créer une séance
        </Button>
      ) : null}
    </div>
  );
}

/** In-person sessions list with type picker and nested tutor create/edit. */
export const SessionsManagementPage: React.FC = () => {
  const {
    canCreate,
    canEditRow,
    canCancelRow,
    canManageAttendance,
    canViewReport,
    canViewReportInformation,
    canJoinInformationMeeting,
    canExportCsv,
    creatableTypes: creatableTypesRaw,
  } = usePageActions('sessions');
  const { elements, isLoading: elementsLoading } = useInterfaceElements('sessions');
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editSessionId, setEditSessionId] = React.useState<string | null>(null);
  const [editGroup, setEditGroup] = React.useState<SessionGroup | null>(null);
  const [editTutorId, setEditTutorId] = React.useState<string | null>(null);
  const [planificationType, setPlanificationType] = React.useState<PlanificationType | null>(null);
  const [planificationModalOpen, setPlanificationModalOpen] = React.useState(false);
  const { viewMode, setViewMode } = useViewMode('sessions-management');
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const contentRef = React.useRef<HTMLDivElement>(null);

  const allowedTabIds = React.useMemo(
    () => resolveAllowedSessionTabs(elements, elementsLoading),
    [elements, elementsLoading],
  );

  /** Types creatable from `action-create-*` RBAC; falls back to allowed tabs. */
  const creatableTabIds = React.useMemo((): SessionTab[] => {
    const TAB_IDS = new Set<SessionTab>(['tutoring', 'information-meeting']);
    if (creatableTypesRaw.length > 0) {
      return creatableTypesRaw.filter((id): id is SessionTab => TAB_IDS.has(id as SessionTab));
    }
    return (allowedTabIds ?? []).filter((id): id is SessionTab => TAB_IDS.has(id as SessionTab));
  }, [creatableTypesRaw, allowedTabIds]);

  const [activeTab, setActiveTab] = React.useState<SessionTab>('tutoring');
  const [tableFilters, setTableFilters] = React.useState<SessionFilters>(DEFAULT_SESSION_FILTERS);
  const [syncOpen, setSyncOpen] = React.useState(false);

  function handleOpenSync(): void {
    setSyncOpen(true);
  }

  function handleCloseSync(): void {
    setSyncOpen(false);
  }

  const tabs: TabItem[] = React.useMemo(() => buildSessionTabItems(allowedTabIds), [allowedTabIds]);

  /** Deep-link: filter the list by tutor passed via location.state. */
  const [personFilter, setPersonFilter] = React.useState<PersonFilter>(() =>
    readPersonFilter(location.state),
  );

  React.useEffect(() => {
    applyPersonFilterState(location.state, location.pathname, setPersonFilter, navigate);
  }, [location.state, location.pathname, navigate]);

  const defaultFilters = React.useMemo(
    () => buildDefaultSessionFilters(personFilter.tutorUserId),
    [personFilter.tutorUserId],
  );

  /** Deep-link: open create/edit/view via URL params. */
  React.useEffect(() => {
    applySessionSearchParams(searchParams, {
      setOpenRow,
      closeRow,
      setEditSessionId,
      setActiveForm,
      setPlanificationModalOpen,
    });
  }, [searchParams, closeRow, setOpenRow]);

  /** Close the form and drop all edit / planification state. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditSessionId(null);
    setEditGroup(null);
    setPlanificationType(null);
    setEditTutorId(null);
  }, []);

  /** Swap the session form for a nested tutor-create form. */
  const handleRequestCreateTutor = React.useCallback(() => {
    setActiveForm('createTutor');
    setEditTutorId(null);
    closeRow();
  }, [closeRow]);

  /** Close the nested tutor form and refresh tutor / onboarding lists. */
  const handleTutorFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditSessionId(null);
    setEditGroup(null);
    setPlanificationType(null);
    setEditTutorId(null);
    void queryClient.invalidateQueries({ queryKey: ['users-search', 'tutor'] });
    void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
  }, [queryClient]);

  const handlePlanificationSelect = (type: PlanificationType) => {
    applyPlanificationSelect(
      type,
      setActiveForm,
      setPlanificationType,
      setPlanificationModalOpen,
      setEditSessionId,
      closeRow,
    );
  };

  /** Open the matching edit form (tutoring vs information-meeting) and close details. */
  const handleEditSession = (sessionId: string, sessionType?: string) => {
    setEditSessionId(sessionId);
    setActiveForm(resolveSessionEditForm(sessionType, activeTab));
    closeRow();
  };

  /** Edit a recurring group via {@link CreateSessionView} (`editGroup`) rather than a single session. */
  const handleEditGroup = (group: SessionGroup) => {
    setEditGroup(group);
    setEditSessionId(null);
    setActiveForm('edit-group');
    closeRow();
  };

  useScrollFormIntoView(activeForm, contentRef);

  /** Refresh session / report queries then close the form (await so the table sees new data). */
  const handleSessionFormSuccess = React.useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['sessions'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['session'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['session-groups'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['session-group'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['reports'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['report'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
    ]);
    setActiveForm(null);
    setEditSessionId(null);
    setEditGroup(null);
    setPlanificationType(null);
    setEditTutorId(null);
  }, [queryClient]);

  /** Stable JSX node for the form — avoids unmounting edit views on re-renders. */
  const formContent = React.useMemo(
    () =>
      renderSessionFormContent({
        activeForm,
        planificationType,
        editSessionId,
        editGroup,
        editTutorId,
        onClose: closeForm,
        onSessionSuccess: handleSessionFormSuccess,
        onTutorSuccess: handleTutorFormSuccess,
        onRequestCreateTutor: handleRequestCreateTutor,
      }),
    [
      activeForm,
      planificationType,
      editSessionId,
      editGroup,
      editTutorId,
      closeForm,
      handleSessionFormSuccess,
      handleTutorFormSuccess,
      handleRequestCreateTutor,
    ],
  );

  /** Open the session-type picker and close any expanded row. */
  function handleOpenCreate(): void {
    setPlanificationModalOpen(true);
    setEditSessionId(null);
    closeRow();
  }

  return (
    <ManagementPage
      title="Gestion des Séances"
      subtitle="Créez, modifiez et gérez toutes vos séances"
      contentRef={contentRef}
      tabs={{
        items: tabs,
        activeTab,
        onTabChange: (id) => {
          closeForm();
          closeRow();
          setActiveTab(id as SessionTab);
        },
        variant: 'colored',
      }}
      actions={renderSessionPageActions(canCreate, handleOpenCreate, handleOpenSync)}
    >
      <PersonFilterBanner
        personName={personFilter.personName}
        onReset={() => {
          setPersonFilter({ tutorUserId: undefined, personName: undefined });
          void navigate(location.pathname, { replace: true, state: {} });
        }}
      />

      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        <SessionsDataTable
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditSession={handleEditSession}
          onEditGroup={handleEditGroup}
          enableFilters={true}
          defaultFilters={defaultFilters}
          onFiltersChange={setTableFilters}
          activeTab={activeTab}
          allowedTabIds={allowedTabIds}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          pageActions={{
            canEditRow,
            canCancelRow,
            canManageAttendance,
            canViewReport,
            canViewReportInformation,
            canJoinInformationMeeting,
            canExportCsv,
          }}
        />
      </CollapseReplaceWrapper>
      <SelectSessionTypeModal
        open={planificationModalOpen}
        onClose={() => setPlanificationModalOpen(false)}
        onSelect={handlePlanificationSelect}
        allowedTabIds={creatableTabIds}
      />
      {CALENDAR_SYNC_ENABLED ? (
        <CalendarSyncModal
          open={syncOpen}
          onClose={handleCloseSync}
          lockedTypes={['session']}
          filterSeed={{
            sessionTab: activeTab,
            sessionFilters: tableFilters,
          }}
        />
      ) : null}
    </ManagementPage>
  );
};
